import { apiService } from '../../../services/api';

export interface UploadResponse {
  document_id: string;
  filename: string;
  file_size: number;
  status: string;
  message: string;
  needs_conversion: boolean;
  pdf_blob?: string;  // Base64 encoded PDF for Word docs
}

export interface ProcessingStatusResponse {
  document_id: string;
  status: 'uploaded' | 'extracting' | 'processing' | 'completed' | 'failed';
  progress_percentage: number;
  estimated_time_remaining?: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface ProcessingResult {
  document_id: string;
  original_filename: string;
  redacted_content: string;
  processing_time: number;
}

class DocumentService {
  
  async uploadDocument(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    // Pass feature_type=provider for Providers/CV-Redactor feature (contact removal only)
    const response = await apiService.getInstance().post('/api/upload/single?feature_type=provider', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  async startProcessing(documentId: string): Promise<{ message: string; status: string }> {
    return await apiService.post(`/api/process/document/${documentId}`);
  }

  async getProcessingStatus(documentId: string): Promise<ProcessingStatusResponse> {
    return await apiService.get(`/api/process/status/${documentId}`);
  }

  async getProcessingResult(documentId: string): Promise<ProcessingResult> {
    return await apiService.get(`/api/process/result/${documentId}`);
  }

  async retryProcessing(documentId: string): Promise<{ message: string; status: string }> {
    return await apiService.post(`/api/process/retry/${documentId}`);
  }

  async getQueueStatus(): Promise<{
    total_documents: number;
    status_counts: Record<string, number>;
    queue_active: boolean;
  }> {
    return await apiService.get('/api/process/queue/status');
  }

  async getProcessingStats(): Promise<any> {
    return await apiService.get('/api/process/stats');
  }

  validateFile(file: File): { valid: boolean; error?: string } {
    const maxSizeInBytes = 50 * 1024 * 1024; // 50MB
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];

    if (!file) {
      return { valid: false, error: 'No file selected' };
    }

    if (file.size > maxSizeInBytes) {
      return { valid: false, error: 'File size exceeds 50MB limit' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'File type not supported. Please use PDF, DOC, or DOCX files.' };
    }

    return { valid: true };
  }
}

export const documentService = new DocumentService();